import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import Svg, {
  Defs,
  LinearGradient as SvgLinearGradient,
  Rect,
  Stop,
} from 'react-native-svg';
import {
  Flex,
  GoodsCard,
  IconAdBadgeOpacity,
  IconChevronRight,
  IconHeartFilled,
  IconPlayarrowFilled,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import type {
  MarketBrandingAdsProps,
  MarketBrandingAdsCardItem,
} from './types';
import type { AnyObject } from '../../../../types';
import { bridge, isWeb, useNavigator } from '../../../../utils';
import { useAddLikeGoods, useOpenDeeplink } from '../../../../hooks';
import { useRemoveLikeGoods } from '../../../../queries';
import { HTMLText } from '../../../HTMLText';
import { getMarketBrandingAdsGoodsData } from './getMarketBrandingAdsGoodsData';

// react-native-video v7 네이티브 전용
const RNVideo = !isWeb ? require('react-native-video') : null;

type VideoState = 'image' | 'preview' | 'full';

const CONTAINER_MARGIN = 16;
const PREMIUM_TEXT_OVERLAP_RATIO = 55 / 193;
const PREMIUM_GRADIENT_RATIO = 99 / 193;

type VideoOverlayProps = {
  videoUrl: string;
  onVideoEnd: () => void;
  onVideoLoad: (duration: number) => void;
};

// 웹: HTML5 <video> 태그
const WebVideoOverlay = ({
  videoUrl,
  onVideoEnd,
  onVideoLoad,
}: VideoOverlayProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const videoRef = useRef<any>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => onVideoEnd();
    const handleLoaded = () => onVideoLoad(video.duration);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('loadedmetadata', handleLoaded);
    video.play().catch(() => {});
    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('loadedmetadata', handleLoaded);
    };
  }, [videoUrl, onVideoEnd, onVideoLoad]);

  return (
    <View style={StyleSheet.absoluteFill}>
      <video
        ref={videoRef}
        src={videoUrl}
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </View>
  );
};

// 네이티브: react-native-video v7
const NativeVideoOverlay = ({
  videoUrl,
  onVideoEnd,
  onVideoLoad,
}: VideoOverlayProps) => {
  const Video = RNVideo!;
  const player = Video.useVideoPlayer(
    { uri: videoUrl },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (p: Record<string, any>) => {
      p.muted = true;
      p.loop = false;
      p.play();
    },
  );

  useEffect(() => {
    const endSub = player.addEventListener('onEnd', onVideoEnd);
    const loadSub = player.addEventListener(
      'onLoad',
      (data: { duration: number }) => {
        onVideoLoad(data.duration);
      },
    );
    return () => {
      endSub.remove();
      loadSub.remove();
    };
  }, [player, onVideoEnd, onVideoLoad]);

  const VideoViewComponent = Video.VideoView;
  return (
    <View style={StyleSheet.absoluteFill}>
      <VideoViewComponent
        player={player}
        style={StyleSheet.absoluteFill}
        resizeMode="cover"
      />
    </View>
  );
};

const VideoOverlay = isWeb ? WebVideoOverlay : NativeVideoOverlay;

export const MarketBrandingAds = ({
  body,
  logging,
  likeMap,
  setLikeMap,
}: MarketBrandingAdsProps) => {
  const { width: screenWidth } = useScreenSize();
  const cardWidth = screenWidth - CONTAINER_MARGIN * 2;
  const { item } = body;
  const isPremium = item.type === 'PREMIUM';
  const gradientId = useRef(
    `mba_grad_${Math.random().toString(36).slice(2, 8)}`,
  ).current;
  const { navigator } = useNavigator();
  const { openDeeplink } = useOpenDeeplink();

  // Figma: LITE coverHeight=98 고정, PREMIUM coverHeight = width × 9/16
  const coverHeight = isPremium ? cardWidth * (9 / 16) : 98;

  // PREMIUM 상품 데이터 변환 (GoodsCard용)
  const goodsData = useMemo(
    () => getMarketBrandingAdsGoodsData(item.goods_list),
    [item.goods_list],
  );

  // 찜 상태 초기화
  useEffect(() => {
    if (!item.goods_list) return;
    setLikeMap(prev => ({
      ...prev,
      ...item.goods_list?.reduce<Record<string, boolean>>((acc, cur) => {
        acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
        return acc;
      }, {}),
    }));
  }, [item.goods_list, setLikeMap]);

  // 찜 추가/제거
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listParams: logging.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prev => ({ ...prev, [sno]: nextValue }));
    },
    screen: (logging.analytics?.SCREEN as string | undefined) ?? '',
  });

  // 로깅 머지 헬퍼
  const createLogPayload = useCallback(
    (
      eventName: string,
      additionalAnalytics?: Record<string, unknown>,
      itemLogging?: AnyObject,
    ) => ({
      name: eventName,
      analytics: {
        ...(logging.analytics as AnyObject),
        ...(body.logging.analytics as AnyObject),
        ...(itemLogging?.analytics as AnyObject),
        ...additionalAnalytics,
      },
      ably_only_log: {
        ...(logging.ably_only_log as AnyObject),
        ...(body.logging.ably_only_log as AnyObject),
        ...(itemLogging?.ably_only_log as AnyObject),
      },
    }),
    [logging, body.logging],
  );

  // 디스플레이 로깅 (1회)
  const displayedRef = useRef(false);
  useEffect(() => {
    if (displayedRef.current) return;
    displayedRef.current = true;
    bridge.logAnalyticsEvent(createLogPayload('DISPLAY_ADS'));
  }, [createLogPayload]);

  // 커버/메인 클릭
  const handlePressCover = useCallback(() => {
    if (!item.deeplink) return;
    bridge.logAnalyticsEvent(createLogPayload('CLICK_ADS', { TYPE: 'MAIN' }));
    openDeeplink(item.deeplink);
  }, [item.deeplink, createLogPayload, openDeeplink]);

  // CTA 버튼 클릭 (PREMIUM)
  const handlePressCta = useCallback(() => {
    if (!item.cta_button) return;
    bridge.logAnalyticsEvent(createLogPayload('CLICK_ADS', { TYPE: 'CTA' }));
    openDeeplink(item.cta_button.deeplink);
  }, [item.cta_button, createLogPayload, openDeeplink]);

  // 카드 클릭 (LITE)
  const handlePressCard = useCallback(
    (cardItem: MarketBrandingAdsCardItem, cardLogging: AnyObject) => {
      bridge.logAnalyticsEvent(
        createLogPayload('CLICK_ADS', { TYPE: 'CARD' }, cardLogging),
      );
      openDeeplink(cardItem.deeplink);
    },
    [createLogPayload, openDeeplink],
  );

  // LITE: 그라데이션 높이를 위한 컨테이너 높이 측정
  const [containerHeight, setContainerHeight] = useState(0);
  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerHeight(e.nativeEvent.layout.height);
  }, []);

  // 동영상 상태 관리 (PREMIUM + video 존재)
  const hasVideo = isPremium && !!item.video;
  const [videoState, setVideoState] = useState<VideoState>('image');
  const previewPlayedRef = useRef(false);
  const videoDurationRef = useRef(0);

  // 마운트 시 프리뷰 자동 재생
  useEffect(() => {
    if (hasVideo && !previewPlayedRef.current) {
      setVideoState('preview');
    }
  }, [hasVideo]);

  // 영상 재생 완료 핸들러
  const handleVideoEnd = useCallback(() => {
    const isPreview = videoState === 'preview';
    bridge.logAnalyticsEvent(
      createLogPayload('COMPLETE_VIDEO', {
        VIDEO_DURATION: videoDurationRef.current,
        IS_PREVIEW: isPreview,
      }),
    );
    if (isPreview) {
      previewPlayedRef.current = true;
    }
    setVideoState('image');
  }, [videoState, createLogPayload]);

  // 풀영상 보기 버튼 클릭
  const handlePressFullVideo = useCallback(() => {
    bridge.logAnalyticsEvent(
      createLogPayload('CLICK_ADS', { TYPE: 'FULL_VIDEO' }),
    );
    setVideoState('full');
  }, [createLogPayload]);

  // 영상 로드 시 duration 저장
  const handleVideoLoad = useCallback((duration: number) => {
    videoDurationRef.current = duration;
  }, []);

  // 현재 재생할 영상 URL
  const videoUrl =
    videoState === 'preview'
      ? item.video?.preview_url
      : videoState === 'full'
        ? item.video?.url
        : undefined;

  if (!item.cover_image_url) return null;

  // 카드/상품 아이템 사이즈: (cardWidth - 좌우 패딩 32 - gap 4) / 3
  const itemWidth = (cardWidth - 32 - 4) / 3;
  // PREMIUM: 텍스트가 커버 이미지를 오버랩하는 높이
  const textOverlap = isPremium
    ? Math.round(coverHeight * PREMIUM_TEXT_OVERLAP_RATIO)
    : 0;

  // LITE: iOS 전체 컨테이너 그라데이션 높이 — 측정값 우선, 폴백은 card_list 기준
  const liteGradientHeight =
    containerHeight || coverHeight + 8 + Math.round(itemWidth / 1.2) + 28 + 20;

  // LITE: 커버 이미지를 카드/상품 중간까지 확장
  const liteCardHalfHeight = Math.round((itemWidth / 1.2 + 28) / 2);
  const liteImageHeight = coverHeight + 8 + liteCardHalfHeight;

  return (
    <Flex margin={{ horizontal: CONTAINER_MARGIN }}>
      <View
        style={styles.outerContainer}
        onLayout={!isPremium ? handleContainerLayout : undefined}
      >
        <Pressable
          onPress={videoState !== 'image' ? undefined : handlePressCover}
        >
          {/* 커버 이미지 + 그라데이션 */}
          <Flex
            overflow={isPremium ? 'hidden' : undefined}
            height={isPremium ? undefined : coverHeight}
            zIndex={isPremium ? undefined : 0}
          >
            <RemoteImage
              uri={item.cover_image_url}
              width={cardWidth}
              height={isPremium ? coverHeight : liteImageHeight}
            />
            {/* 동영상 오버레이 — 커버 이미지 위, 그라데이션 아래 (PREMIUM + video) */}
            {hasVideo && videoState !== 'image' && videoUrl && (
              <VideoOverlay
                videoUrl={videoUrl}
                onVideoEnd={handleVideoEnd}
                onVideoLoad={handleVideoLoad}
              />
            )}
            {/* LITE: 딤 오버레이 — 커버 이미지 높이에 맞춤 */}
            {!isPremium && (
              <View
                style={[{ position: 'absolute', top: 0, left: 0, right: 0, height: liteImageHeight }, styles.liteDim]}
                pointerEvents="none"
              />
            )}
            {/* PREMIUM: 커버 하단 그라데이션, LITE: 전체 컨테이너 그라데이션 (커버 밖으로 확장) */}
            <View
              style={
                isPremium
                  ? [
                      styles.gradient,
                      {
                        height: Math.round(
                          coverHeight * PREMIUM_GRADIENT_RATIO,
                        ),
                      },
                    ]
                  : [styles.liteGradient, { height: liteGradientHeight }]
              }
              pointerEvents="none"
            >
              <Svg width="100%" height="100%">
                <Defs>
                  <SvgLinearGradient
                    id={gradientId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <Stop
                      offset={isPremium ? '0' : '0'}
                      stopColor="#000000"
                      stopOpacity="0"
                    />
                    <Stop
                      offset={isPremium ? '1' : '0.6'}
                      stopColor="#000000"
                      stopOpacity="1"
                    />
                  </SvgLinearGradient>
                </Defs>
                <Rect width="100%" height="100%" fill={`url(#${gradientId})`} />
              </Svg>
            </View>
            {/* LITE: 텍스트 — 커버 영역 안, 하단 absolute (iOS: contentsStackView) */}
            {!isPremium && (
              <View style={styles.liteTextOverlay}>
                <HTMLText type="subtitle5" color="content_disabled">
                  {item.overline}
                </HTMLText>
                <Flex height={4} />
                <Flex.Row alignItems="center">
                  <HTMLText type="h4" color="content_always_white">
                    {item.title}
                  </HTMLText>
                  <IconChevronRight
                    width={16}
                    height={16}
                    color="content_always_white"
                  />
                </Flex.Row>
                <Flex height={8} />
                {item.informations.tags ? (
                  <Flex.Row alignItems="center" gap={2}>
                    <IconHeartFilled
                      width={12}
                      height={12}
                      color="content_tertiary"
                    />
                    <HTMLText type="body4" color="content_tertiary">
                      {item.informations.tags}
                    </HTMLText>
                  </Flex.Row>
                ) : null}
              </View>
            )}
            {/* "풀영상 보기" 버튼 — image 상태 + video 존재 시 커버 중앙 (iOS 동일) */}
            {hasVideo && videoState === 'image' && (
              <View
                style={styles.fullVideoButtonWrapper}
                pointerEvents="box-none"
              >
                <Pressable
                  onPress={handlePressFullVideo}
                  style={styles.fullVideoButton}
                >
                  <IconPlayarrowFilled width={16} height={16} fill="#FFFFFF" />
                  <Typography type="body4" color="content_always_white">
                    풀영상 보기
                  </Typography>
                </Pressable>
              </View>
            )}
          </Flex>

          {/* 텍스트 콘텐츠 — PREMIUM만 (LITE 텍스트는 커버 Flex 안 absolute) */}
          {isPremium && (
            <Flex
              padding={{ horizontal: 20, top: 12, bottom: 8 }}
              margin={{ top: -textOverlap }}
            >
              {/* 마켓정보 + CTA 같은 행 */}
              <Flex.Row alignItems="flex-end">
                <Flex flex={1}>
                  <HTMLText type="subtitle5" color="content_always_white">
                    {item.overline}
                  </HTMLText>
                  <Flex height={4} />
                  <HTMLText type="h4" color="content_always_white">
                    {item.title}
                  </HTMLText>
                  <Flex height={8} />
                  {item.informations.tags ? (
                    <Flex.Row alignItems="center" gap={2}>
                      <IconHeartFilled
                        width={12}
                        height={12}
                        color="content_tertiary"
                      />
                      <HTMLText type="body4" color="content_tertiary">
                        {item.informations.tags}
                      </HTMLText>
                    </Flex.Row>
                  ) : null}
                </Flex>
                {/* CTA — iOS: subtitle3, content_alwayswhite, borderWidth 0.5 */}
                {item.cta_button && (
                  <Pressable onPress={handlePressCta}>
                    <Flex
                      borderRadius={16}
                      borderWidth={0.5}
                      borderColorValue="rgba(255, 255, 255, 0.5)"
                      padding={{ horizontal: 12, vertical: 8 }}
                    >
                      <Typography type="subtitle3" color="content_always_white">
                        {item.cta_button.title}
                      </Typography>
                    </Flex>
                  </Pressable>
                )}
              </Flex.Row>
              {/* description */}
              {item.informations.description ? (
                <>
                  <Flex height={12} />
                  <HTMLText type="body3" color="content_disabled">
                    {item.informations.description}
                  </HTMLText>
                </>
              ) : null}
            </Flex>
          )}
        </Pressable>

        {/* 카드 리스트 — iOS: cardList.isNotEmpty이면 타입 무관 렌더 */}
        {item.card_list && item.card_list.length > 0 && (
          <Flex
            padding={{
              horizontal: 16,
              top: 8,
              bottom: 20,
            }}
          >
            {/* iOS: contentStackView에 cornerRadius=4 (개별 카드가 아닌 컨테이너) */}
            <Flex borderRadius={4} overflow="hidden">
              <Flex.Row gap={2}>
                {item.card_list.slice(0, 3).map((card, index) => (
                  <Pressable
                    key={index}
                    onPress={() => handlePressCard(card.item, card.logging)}
                    style={styles.cardPressable}
                  >
                    <Flex backgroundColorValue="rgba(255, 255, 255, 0.1)">
                      <RemoteImage
                        uri={card.item.thumbnail_image_url}
                        width={itemWidth}
                        height={itemWidth / 1.2}
                      />
                      {/* iOS: titleLabel bottom inset 6px — 28px 영역에서 하단 정렬 */}
                      <Flex
                        height={28}
                        justifyContent="flex-end"
                        alignItems="center"
                        padding={{ horizontal: 6, bottom: 6 }}
                      >
                        <HTMLText
                          type="body3"
                          color="content_always_white"
                          numberOfLines={1}
                          textAlign="center"
                        >
                          {card.item.title}
                        </HTMLText>
                      </Flex>
                    </Flex>
                  </Pressable>
                ))}
              </Flex.Row>
            </Flex>
          </Flex>
        )}

        {/* 상품 리스트 — iOS: goodsList.isNotEmpty이면 타입 무관 렌더 */}
        {goodsData.length > 0 && (
          <Flex
            padding={{
              horizontal: 16,
              top: 8,
              bottom: 10,
            }}
          >
            <Flex
              {...(isPremium
                ? { borderTopLeftRadius: 4, borderTopRightRadius: 4 }
                : { borderRadius: 4 })}
              overflow="hidden"
            >
              <Flex.Row gap={2}>
                {goodsData.map(data => (
                  <Flex flex={1} key={data.sno}>
                    <GoodsCard
                      data={data}
                      size="medium"
                      themeType="dark"
                      defaultWidth={itemWidth}
                      isLike={likeMap[data.sno]}
                      onPress={() => {
                        const deeplink = data.item.remote_deeplink;
                        if (deeplink) {
                          bridge.logAnalyticsEvent(
                            createLogPayload(
                              'CLICK_ADS',
                              { TYPE: 'GOODS' },
                              data.logging,
                            ),
                          );
                          navigator.moveToGoods({
                            sno: data.item.sno,
                            firstPageRendering:
                              data.item.first_page_rendering ?? null,
                            remoteDeeplink:
                              data.item.remote_deeplink ?? undefined,
                            loggingAnalytics: data.logging.analytics,
                          });
                        }
                      }}
                      onPressLike={async () => {
                        const currentLike = likeMap[data.sno] ?? false;
                        bridge.logAnalyticsEvent({
                          name: currentLike
                            ? 'CLICK_UNLIKE_GOODS'
                            : 'CLICK_LIKE_GOODS',
                          analytics: data.logging.analytics,
                          ably_only_log: data.logging.ably_only_log,
                        });
                        if (currentLike) {
                          await mutateRemoveLikeGoods({
                            sno: data.item.sno,
                            list_params_referrer: data.logging.analytics
                              ?.LIST_ID as string | undefined,
                          });
                          bridge.likeGoods({
                            goods_sno: data.item.sno,
                            is_liked: false,
                          });
                          setLikeMap(prev => ({
                            ...prev,
                            [data.sno]: false,
                          }));
                          return;
                        }
                        addLikeGoods(
                          data.item.sno,
                          data.logging.analytics,
                          data.logging.ably_only_log,
                        );
                      }}
                    />
                  </Flex>
                ))}
              </Flex.Row>
            </Flex>
          </Flex>
        )}

        {/* AD 배지 — iOS: icon_ad_badge_opacity SVG */}
        <View
          style={[styles.adBadge, { top: isPremium ? 17 : 25 }]}
          pointerEvents="none"
        >
          <IconAdBadgeOpacity fill="#000000" />
        </View>
      </View>
    </Flex>
  );
};

const styles = StyleSheet.create({
  // PREMIUM 그라데이션 — 커버 하단 절대 위치
  gradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  // LITE 딤 오버레이
  liteDim: {
    backgroundColor: 'rgba(18, 18, 18, 0.7)',
  },
  // LITE 그라데이션 — 전체 컨테이너 높이, 커버 Flex 밖으로 확장
  liteGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  // LITE 텍스트 오버레이 — 커버 영역 안 하단 (iOS: contentsStackView)
  liteTextOverlay: {
    position: 'absolute',
    bottom: 8,
    left: 16,
  },
  // 외부 컨테이너
  outerContainer: {
    backgroundColor: 'black',
    borderRadius: 16,
    overflow: 'hidden' as const,
  },
  // AD 배지 — iOS: icon_ad_badge_opacity SVG (배경+텍스트 포함)
  adBadge: {
    position: 'absolute',
    right: 20,
  },
  cardPressable: {
    flex: 1,
  },
  // "풀영상 보기" 버튼 래퍼 — 커버 중앙
  fullVideoButtonWrapper: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // "풀영상 보기" 버튼 — iOS: 94×40, dark blur alpha 0.4, cornerRadius 20
  fullVideoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 94,
    height: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: 20,
    gap: 2,
  },
});
